"use client";

import { useRef, useState, useEffect } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore, Persistor } from "redux-persist";
import { makeStore, AppStore } from "@lib/redux/store";
import { PersistorContext } from "@lib/redux/PersistorContext";

export default function StoreProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const storeRef = useRef<AppStore | null>(null);
  const persistorRef = useRef<Persistor | null>(null);
  const [isClient, setIsClient] = useState(false); // State to track client mount

  // Initialize store and persistor only once on the client
  // This logic remains the same
  if (!storeRef.current) {
    const store = makeStore();
    storeRef.current = store;
    persistorRef.current = persistStore(store);
  }

  // Use useEffect to set isClient to true after mounting
  useEffect(() => {
    setIsClient(true);
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <Provider store={storeRef.current!}>
      <PersistorContext.Provider value={persistorRef.current!}>
        {/* Conditionally render PersistGate only on the client */}
        {isClient ? (
          <PersistGate loading={null} persistor={persistorRef.current!}>
            {children}
          </PersistGate>
        ) : (
          // Render children directly during SSR or before client mount/hydration
          // Or provide a specific server-side loading state if desired
          children
        )}
      </PersistorContext.Provider>
    </Provider>
  );
}
